import { useSessionKey } from "@auth/sessionStore";
import type {
  Connector,
  ConnectorStatus,
  ConnectorUpdateOutcome,
} from "@connector/connectorClient";
import {
  useConnectorsList,
  useUnpairConnector,
} from "@connector/connectorQueries";
import { useUser } from "@context/UserContext";
import {
  faPlugCircleXmark,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  FieldError,
  LabelTape,
  SettingsCard,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime, formatRelativeTimeMs } from "@utils/format-date";

interface ConnectorRowProps {
  connector: Connector;
  unpairing: boolean;
  onUnpair: (id: string) => void;
}

/** Offline goes unmarked: a switched-off machine is a fact, not an alarm. */
const STATUS_WORD: Record<
  ConnectorStatus,
  { label: string; tone: SwipeTone | null }
> = {
  connected: { label: "Connected", tone: "working" },
  updating: { label: "Updating…", tone: "flag" },
  offline: { label: "Offline", tone: null },
};

/** Failures only; `rollback_failed` may mean a machine running nothing, so it must not read like `rolled_back`. */
const FAILED_UPDATE: Partial<Record<ConnectorUpdateOutcome, string>> = {
  rolled_back: "Update failed, rolled back",
  rollback_failed: "Update failed, rollback failed",
  download_failed: "Couldn't download update",
};

/** An applier killed mid-swap leaves its in-flight row forever; once brian stops calling it Updating…, that row means abandoned. */
const ABANDONED_UPDATE: Partial<Record<ConnectorUpdateOutcome, string>> = {
  applying: "Update never finished",
  rolling_back: "Rollback never finished",
};

const failedUpdate = (
  status: ConnectorStatus,
  lastUpdate: Connector["lastUpdate"],
) => {
  if (!lastUpdate) return null;
  const label =
    FAILED_UPDATE[lastUpdate.outcome] ??
    (status === "updating" ? undefined : ABANDONED_UPDATE[lastUpdate.outcome]);
  if (!label) return null;
  const target = lastUpdate.toVersion;
  const text = target ? `${label} (v${target})` : label;
  const when = Date.parse(lastUpdate.occurredAt);
  const dated = !Number.isNaN(when);
  return {
    label: text,
    // `occurred_at` is the clinic box's unbounded clock; clamp so a fast clock can't read "in 1 hour".
    when: dated ? formatRelativeTimeMs(Math.min(when, Date.now())) : null,
    // `detail` is the `connectors_ctl` error, the only account of why.
    title: [
      text,
      lastUpdate.detail,
      dated ? formatDateTime(lastUpdate.occurredAt) : null,
    ]
      .filter(Boolean)
      .join(" — "),
  };
};

const ConnectorRow = ({
  connector,
  unpairing,
  onUnpair,
}: ConnectorRowProps) => {
  const { label, tone } = STATUS_WORD[connector.status];
  const failure = failedUpdate(connector.status, connector.lastUpdate);
  return (
    <li className="py-3" data-testid="connector-row">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <LabelTape className="max-w-full truncate">
            {connector.name?.trim() || connector.hostname}
          </LabelTape>
          <div className="flex items-center gap-2 text-xs">
            {tone ? (
              <Swipe tone={tone}>{label}</Swipe>
            ) : (
              <span className="text-annotation">{label}</span>
            )}
            <span className="font-mono text-annotation">
              v{connector.agentVersion}
            </span>
          </div>
          {failure && (
            <div
              className="flex max-w-full min-w-0 items-center gap-1.5 text-xs"
              data-testid="connector-update-failure"
              title={failure.title}
            >
              <Swipe
                tone="stopped"
                icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                className="min-w-0"
              >
                <span className="truncate">{failure.label}</span>
              </Swipe>
              {failure.when && (
                <span className="shrink-0 text-annotation">{failure.when}</span>
              )}
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onUnpair(connector.id)}
          busy={unpairing}
          data-testid="connector-unpair"
        >
          <FontAwesomeIcon icon={faPlugCircleXmark} aria-hidden="true" />
          {unpairing ? "Removing" : "Unpair"}
        </Button>
      </div>
    </li>
  );
};

export const ConnectorsCard = () => {
  const { isUserAuthed } = useUser();
  const sessionKey = useSessionKey();
  const connectorsQuery = useConnectorsList(sessionKey, isUserAuthed);
  const unpair = useUnpairConnector(sessionKey);

  const connectors = connectorsQuery.data ?? [];
  const loading = isUserAuthed && connectorsQuery.isLoading;
  const error = isUserAuthed && connectorsQuery.isError;

  return (
    <SettingsCard
      title="Clinic computers"
      subtitle="Each paired clinic computer and whether it's connected right now."
      data-testid="connector-list-card"
    >
      <div className="flex flex-col gap-4">
        {!isUserAuthed && (
          <p className="text-sm text-annotation">
            Select your profile to see paired clinic computers.
          </p>
        )}

        {loading && <p className="text-sm text-annotation">Checking…</p>}

        {error && (
          <FieldError data-testid="connector-list-error">
            Couldn't reach the server.
          </FieldError>
        )}

        {isUserAuthed && !loading && !error && connectors.length === 0 && (
          <p
            data-testid="connector-list-empty"
            className="text-sm text-annotation"
          >
            No clinic computers paired yet. Generate a code below and run the
            connector on the clinic computer.
          </p>
        )}

        {connectors.length > 0 && (
          <ul
            className="divide-y divide-rule border-y border-rule"
            data-testid="connector-list"
          >
            {connectors.map((c) => (
              <ConnectorRow
                key={c.id}
                connector={c}
                unpairing={unpair.isPending && unpair.variables === c.id}
                onUnpair={unpair.mutate}
              />
            ))}
          </ul>
        )}

        {unpair.isError && (
          <FieldError data-testid="connector-unpair-error">
            Couldn't remove that connection. Try again.
          </FieldError>
        )}
      </div>
    </SettingsCard>
  );
};
